import { Location, CurrencyPipe, CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  AfterContentChecked,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { StoreService } from 'src/app/core/services/store.service';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { SnackBarService } from 'src/app/core/services/snackbar.service';
import { HeaderComponent } from 'src/app/shared/components/header/header.component';

declare let ZestPayPop: any;

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
  standalone: true,
  imports: [CurrencyPipe, HeaderComponent, CommonModule, RouterModule],
})
export class CartComponent implements OnInit, AfterContentChecked {
  @Input() open: any;

  disable: boolean = false;
  pageLoad: boolean = false;
  storeName!: string;
  showShipping: boolean = false;
  deliveryFee: any;
  orderData: any;
  loading: any;
  shippingOption: string = '';
  regionOptions: any;
  orderObj: any;
  deliveryRates: any;
  orderCreated: any;
  ratesFetched: any;
  deliveryAmount: any;
  selectedCourier: any;
  stores: any;
  callbackResponse: any;
  publicKey: string = '';
  merchant: string = '';
  createStoreOrderError: string = '';
  currentStore: any;
  storeInfo: any;
  pickup: any;

  /**
   * cartItems is kept as a getter-backed property so the template always
   * reflects the live store state. The private backing field is only used
   * to seed the initial value from localStorage.
   */
  private _cartItems: any[] | null = null;

  get cartItems(): any[] | null {
    return this._cartItems;
  }

  /** True only when there is at least one item — use this in the parent template too */
  get hasItems(): boolean {
    return Array.isArray(this._cartItems) && this._cartItems.length > 0;
  }

  singleStore!: { storename: string };

  constructor(
    public store: StoreService,
    private location: Location,
    public dialog: MatDialogModule,
    public router: Router,
    public snackBar: SnackBarService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
  ) {
    // Seed from localStorage on construction
    this._cartItems = JSON.parse(
      localStorage.getItem(this.store.getStoreCartKey('cart'))!,
    );
  }

  onBackClick(): void {
    this.location.back();
  }

  add(a: number, b: number): number {
    return a + b;
  }

  total(): number {
    if (this.deliveryAmount && this.shippingOption !== 'PICKUP_IN_STORE') {
      return this.store.reduceCartPrice() + this.deliveryAmount;
    }
    return this.store.reduceCartPrice();
  }

  plus(product: any, variant: string, price: number): void {
    product.variants?.forEach((variantsObj: any) => {
      if (variantsObj.name === variant) {
        if (
          this.store.getVariantCount(variantsObj) === 0 &&
          variantsObj.stock === 0
        ) {
          this.snackBar.openSnackBar(
            'Sorry, this product is no longer in stock',
            'error-snackbar',
          );
        } else if (
          variantsObj.stock > this.store.getVariantCount(variantsObj)
        ) {
          this.store.addVariantToCart(product, 1, variant, price, variantsObj);
        } else {
          this.store.qunatityExceeded = true;
        }
      }
    });
  }

  minus(product: any, variantObj: any): void {
    if (this.store.getVariantCount(variantObj) !== 0) {
      this.store.subtractVariantFromCart(product, variantObj);
    }
  }

  updateRadioState($event: any, rate: any): void {
    const value = $event.target.value;
    this.selectedCourier = {
      parcel_id: rate.parcel_id,
      courier: rate.carrier,
      shipping_fee: rate.amount,
      rate_id: rate.rate_id,
      order_id: rate.order_id,
      delivery_time: rate.delivery_time,
    };
    this.deliveryAmount = +value;
  }

  // ─── Order creation ────────────────────────────────────────────────────────

  createOrderObj(): object {
    return (this.orderObj = {
      order_details: this._cartItems?.map((cartItem: any) => ({
        product: { product_code: cartItem.product.product_code },
        variant_name: cartItem.variantPurchased
          ? cartItem.variantPurchased.name
          : null,
        product_quantity: cartItem.cartQuantity,
        unit_price: cartItem.variantPurchased
          ? cartItem.variantPurchased.price
          : cartItem.product.discounted_price
            ? cartItem.product.discounted_price
            : cartItem.product.price,
        subtotal:
          cartItem.cartQuantity *
          (cartItem.variantPurchased
            ? cartItem.variantPurchased.price
            : cartItem.product.discounted_price
              ? cartItem.product.discounted_price
              : cartItem.product.price),
      })),
      order: {
        total_price: this.total(),
        quantity: this._cartItems?.length,
      },
    });
  }

  handleFormValuesData($event: any): void {
    this.orderData = $event;

    if (!$event) return;

    if (this.orderData?.addressDTO?.region) {
      for (const location of this.regionOptions) {
        if (this.orderData?.addressDTO?.region === location.area) {
          this.deliveryFee = location.fee;
          this.deliveryAmount = this.deliveryFee;
        }
      }

      this.loading = true;
      this.store.createOrder(this.orderData).subscribe({
        next: (response: any) => {
          if (this.shippingOption === 'I_WILL_MANAGE_MY_OWN_SHIPPING') {
            this.loading = false;
            this.orderCreated = response.data.orderResponseDetailList;
            this.selectedCourier = {
              parcel_id: '',
              courier: '',
              shipping_fee: 0,
              rate_id: '',
              delivery_time: '',
              order_id: this.orderCreated.order.order_code,
            };
          }
        },
        error: (error: any) => {
          this.snackBar.openSnackBar(
            `${error.error.message}`,
            'error-snackbar',
          );
        },
      });
    } else {
      if (this.shippingOption === 'USE_ZEST_SHIPPING_PARTNERS') {
        this.ratesFetched = true;
        this.snackBar.openSnackBar(
          'Fetching shipping rates',
          'success-snackbar',
        );
        this.store.createOrder(this.orderData).subscribe({
          next: (response: any) => {
            this.deliveryRates = response.data.orderResponseZestPartnerList;
            this.ratesFetched = false;
            this.snackBar.openSnackBar(
              'Rates fetched successfully',
              'success-snackbar',
            );
          },
          error: (error: any) => {
            this.ratesFetched = false;
            this.snackBar.openSnackBar(
              `${error.error.message}`,
              'error-snackbar',
            );
          },
        });
      }

      if (this.shippingOption === 'PICKUP_IN_STORE') {
        this.store.createOrder(this.orderData).subscribe({
          next: (response: any) => {
            this.loading = false;
            this.pickup = response.data.orderResponseDetailList;
            this.deliveryAmount = 1;
            this.selectedCourier = {
              parcel_id: '',
              courier: '',
              shipping_fee: 0,
              rate_id: '',
              delivery_time: '',
              order_id: this.pickup.order.order_code,
            };
          },
          error: (error: any) => {
            this.loading = false;
            this.snackBar.openSnackBar(
              `${error.error.message}`,
              'error-snackbar',
            );
          },
        });
      }
    }
  }

  // ─── Post-order ────────────────────────────────────────────────────────────

  updateShipppingInfo(ref: any): void {
    this.store.createStoreOrder(this.selectedCourier, ref).subscribe({
      next: (_response: any) => {
        this.postOrderAction();
      },
      error: (err: any) => {
        this.snackBar.openSnackBar(`${err.error.message}`, 'error-snackbar');
      },
    });
  }

  postOrderAction(): void {
    this.snackBar.openSnackBar(
      'Payment successful, your order has been sent!',
      'success-snackbar',
    );
    this._cartItems = [];
    this.store.cartItems = [];
    localStorage.removeItem(this.store.getStoreCartKey('cart'));
    localStorage.removeItem(this.store.getStoreCartKey('cartTotal'));
    window.location.assign(`/stores/${this.storeName}`);
  }

  // ─── Payment ───────────────────────────────────────────────────────────────

  getMerchantKey(merchant: string): void {
    this.store.getAPIKEY(merchant).subscribe({
      next: (res: any) => {
        this.publicKey = res;
      },
    });
  }

  payWithZest(): void {
    const handler = ZestPayPop.setup({
      key: this.publicKey,
      email: this.orderData.addressDTO.email,
      amount: this.total() * 100,
      onClose: () => {},
      callback: (response: any) => {
        this.callbackResponse = response;
        this.updateShipppingInfo(this.callbackResponse.reference);
      },
    });
    handler.openIframe();
  }

  // ─── Store loading ─────────────────────────────────────────────────────────

  getStore(store: string): void {
    this.store.findStoreByName(store).subscribe({
      next: (res: any) => {
        this.stores = res;
        this.pageLoad = true;
        this.shippingOption = this.stores.shipping_method;

        if (this.shippingOption === null) {
          this.storeInfo = {
            shipping: null,
            email: res.email,
            phone: res.phone_number,
          };
        } else if (this.shippingOption === 'PICKUP_IN_STORE') {
          this.storeInfo = {
            shipping: 'PICKUP_IN_STORE',
            email: res.email,
            phone: res.phone_number,
          };
        } else if (this.shippingOption === 'I_WILL_MANAGE_MY_OWN_SHIPPING') {
          this.store.getOwnShipping().subscribe({
            next: (ownRes: any) => {
              this.regionOptions = ownRes.data;
              this.storeInfo =
                this.regionOptions.length > 0
                  ? null
                  : {
                      shipping: 'I_WILL_MANAGE_MY_OWN_SHIPPING',
                      email: this.stores.email,
                      phone: this.stores.phone_number,
                    };
            },
          });
        } else {
          this.storeInfo = null;
        }

        this.currentStore = {
          store_code: this.stores.store_code,
          store_name: this.stores.slug,
        };
        this.store.checkStoreCart(this.stores.store_code);
        sessionStorage.setItem(
          'currentStore',
          JSON.stringify(this.currentStore),
        );
        this.storeName = JSON.parse(
          sessionStorage.getItem('currentStore')!,
        ).store_name.replace(/\s/g, '-');
        this.getMerchantKey(res.merchantId);
      },
      error: (err: any) => {
        if (err.error.status === 404) {
          this.snackBar.openSnackBar(
            'Store does not exist, please check the link and try again.',
            'error-snackbar',
          );
        }
        this.pageLoad = true;
        this.router.navigate(['/']);
      },
    });
  }

  private getStoreNameFromSessionStorage(): void {
    const currentStore = sessionStorage.getItem('currentStore');
    if (currentStore) {
      this.storeName = JSON.parse(currentStore).store_name;
    }
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.getStoreNameFromSessionStorage();
  }

  ngAfterContentChecked(): void {
    // Always sync cartItems from the live store so removals reflect immediately
    const liveItems = this.store.getcartItems();
    this._cartItems = liveItems ?? [];

    // Clean up localStorage when cart is empty
    if (Array.isArray(this._cartItems) && this._cartItems.length === 0) {
      localStorage.removeItem(this.store.getStoreCartKey('cart'));
    }

    this.cdr.markForCheck();
  }
}
